// Task #39 — sticky desktop navigation rail for the Almanac report.
//
// Visible at ≥1280px viewport only (CSS-gated; the React component still
// renders but its container is display:none below the breakpoint). Three
// stacked regions inside a 220px sticky column:
//
//   1. Header — "LEAGUE ALMANAC" eyebrow + league name + year range
//   2. Nav list — every section that's currently rendering, with the
//      active section highlighted as the reader scrolls
//   3. Download JSON anchor — persistent CTA at the bottom of the rail
//
// Conditional sections (Drafts, Trades) only appear in the list when
// stats.drafts / stats.trades have content. The rail filters its own list
// against `availableSections` rather than rendering dead links.
//
// Internal scroll: when the viewport is shorter than the rail's content
// (laptop with ~700px height), the nav region scrolls while the header
// and Download CTA stay pinned via flex layout.

import { useEffect, useState } from "react";
import theme from "../../data/theme";

// A1.5 §2.3 — section order matches the report's new scroll order.
// Career Totals dropped (now the full-width featured card above the
// section list). Share Cards dropped (component temporarily removed
// per §2.5). Awards + Playoffs moved up after Championships.
// `key` matches the section anchor id (recap-section-${key}).
// `conditional` keys hide when availableSections[key] is falsy.
const NAV_ITEMS = [
  { key: "overview",     label: "Overview" },
  { key: "championships",label: "Championships" },
  { key: "awards",       label: "Awards" },
  { key: "playoffs",     label: "Playoffs" },
  { key: "drafts",       label: "Drafts",       conditional: true },
  { key: "keepers",      label: "Keepers",      conditional: true },
  { key: "trades",       label: "Trades",       conditional: true },
  { key: "singleGame",   label: "Single-Game" },
  { key: "streaks",      label: "Streaks" },
  { key: "singleSeason", label: "Single-Season" },
  { key: "pointsPerGame",label: "Points Per Game" },
  { key: "formatHistory",label: "Format History",  conditional: true },
  { key: "headToHead",   label: "Head-to-Head" },
  { key: "luck",         label: "Luck" },
  { key: "volatility",   label: "Volatility" },
  { key: "yoySwings",    label: "YoY Swings" },
];

export default function AlmanacSidebar({
  leagueName,
  yearRange,
  availableSections,
  onJumpToSection,
  downloadButton,
}) {
  const items = NAV_ITEMS.filter(it => !it.conditional || availableSections[it.key]);
  const activeKey = useActiveSection(items.map(it => it.key));

  const handleClick = (e, key) => {
    e.preventDefault();
    onJumpToSection?.(key);
  };

  return (
    <aside className="recap-sidebar" aria-label="Almanac navigation">
      <div className="recap-sidebar-inner">
        <div className="recap-sidebar-header">
          {/* A1 Patch B — sidebar title block bumped +2px each line. */}
          <div style={{
            fontSize: 12, letterSpacing: 2.5, color: theme.colors.brand.accent,
            textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
            marginBottom: 5,
          }}>League Almanac</div>
          <div style={{
            fontSize: 16, fontWeight: theme.fontWeight.heavy,
            color: theme.colors.text.primary, lineHeight: 1.3,
            wordBreak: "break-word",
          }}>{leagueName || "Your League"}</div>
          {yearRange && (
            <div style={{
              fontSize: 13, color: theme.colors.text.muted,
              fontFamily: theme.fonts.body, marginTop: 5,
            }}>{yearRange}</div>
          )}
        </div>

        <nav className="recap-sidebar-nav" aria-label="Sections">
          <ul style={{
            listStyle: "none", margin: 0, padding: 0,
            display: "flex", flexDirection: "column", gap: 2,
          }}>
            {items.map(it => {
              const isActive = activeKey === it.key;
              return (
                <li key={it.key}>
                  <a
                    href={`#recap-section-${it.key}`}
                    onClick={(e) => handleClick(e, it.key)}
                    className={`recap-sidebar-link${isActive ? " recap-sidebar-link--active" : ""}`}
                  >
                    {it.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {downloadButton && (
          <div className="recap-sidebar-cta">
            {downloadButton}
          </div>
        )}
      </div>
    </aside>
  );
}

// Tracks which of `sectionKeys` is currently centered in the viewport.
// rootMargin clips the observer's effective viewport to the middle 20%,
// so a section is "active" when it crosses through the screen's centerline.
// This feels more responsive than a strict threshold — the highlight
// updates as the user scrolls between sections instead of waiting for one
// to occupy a majority of the visible area.
function useActiveSection(sectionKeys) {
  const [active, setActive] = useState(sectionKeys[0] || null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;
    if (!sectionKeys.length) return;

    // Track every section that's currently intersecting; pick the topmost
    // one (smallest top offset) as "active". Without this, fast scrolls
    // can leave the highlight stuck on whichever section last fired its
    // `isIntersecting=true` event.
    const intersecting = new Set();
    const observer = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const key = e.target.id?.replace(/^recap-section-/, "");
        if (!key) continue;
        if (e.isIntersecting) intersecting.add(key);
        else intersecting.delete(key);
      }
      if (intersecting.size > 0) {
        // Find the entry with the smallest top — the topmost active section.
        let bestKey = null;
        let bestTop = Infinity;
        for (const k of intersecting) {
          const el = document.getElementById(`recap-section-${k}`);
          if (!el) continue;
          const top = el.getBoundingClientRect().top;
          if (top < bestTop) { bestTop = top; bestKey = k; }
        }
        if (bestKey) setActive(bestKey);
      }
    }, {
      rootMargin: "-40% 0px -40% 0px",
      threshold: 0,
    });

    const els = [];
    for (const key of sectionKeys) {
      const el = document.getElementById(`recap-section-${key}`);
      if (el) { observer.observe(el); els.push(el); }
    }

    return () => observer.disconnect();
    // sectionKeys is recomputed on each parent render; stringify for stable dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKeys.join("|")]);

  return active;
}
